import ExcelJS from 'exceljs';
import { difference, get } from 'lodash';

import { ExportSchemaItem, XlsxColumn } from './types';

/**
 * 进度提取结果
 */
export interface ProgressExtractionResult {
  /** 提取后的纯净数据 */
  cleanData: Uint8Array;
  /** 最后一个进度值（如果有） */
  progress: number | null;
}

/**
 * 创建进度消息 Buffer
 *
 * 进度协议：固定 4 字节 "PP|-" 格式
 * - PP: 2位数字 (00-99)
 * - 00 代表 100%，01-99 代表实际进度 1%-99%
 *
 * @param progress 进度值 (0-100)
 * @returns 进度消息的 Buffer
 *
 * @example
 * ```typescript
 * createProgressMessage(0)   // → Buffer.from('01|-')  // 0% 映射到 1%
 * createProgressMessage(1)   // → Buffer.from('01|-')
 * createProgressMessage(50)  // → Buffer.from('50|-')
 * createProgressMessage(99)  // → Buffer.from('99|-')
 * createProgressMessage(100) // → Buffer.from('00|-')  // 100% 编码为 00
 * ```
 */
export function createProgressMessage(progress: number): Buffer {
  // 100 编码为 "00|-"（代表 100%）
  // 0-99 映射到 1-99（确保至少为 1）
  const progressValue = progress === 100 ? 0 : Math.max(1, Math.min(99, progress));
  return Buffer.from(`${progressValue.toString().padStart(2, '0')}|-`);
}

/**
 * 从流数据中提取进度信息并返回纯净的 Excel 数据
 *
 * 进度协议：固定 4 字节 "PP|-" 格式
 * - PP: 2位数字 (00-99)
 * - 00 代表 100%，01-99 代表实际进度
 *
 * @param chunk 原始数据块
 * @returns 纯净数据和进度信息
 *
 * @example
 * ```typescript
 * const { cleanData, progress } = extractProgressFromChunk(value);
 * if (progress !== null) {
 *   setProgress(progress);
 * }
 * if (cleanData.length > 0) {
 *   await writer.write(cleanData);
 * }
 * ```
 */
export function extractProgressFromChunk(chunk: Uint8Array): ProgressExtractionResult {
  const segments: Uint8Array[] = [];
  let lastProgressValue: number | null = null;
  let writeStart = 0;
  let searchPos = 0;

  // 扫描整个 chunk，查找所有进度消息
  while (searchPos + 4 <= chunk.length) {
    const byte1 = chunk[searchPos];
    const byte2 = chunk[searchPos + 1];
    const byte3 = chunk[searchPos + 2];
    const byte4 = chunk[searchPos + 3];

    // 检查是否符合 "PP|-" 格式
    const isProgressMessage =
      byte1 >= 48 &&
      byte1 <= 57 && // '0'-'9'
      byte2 >= 48 &&
      byte2 <= 57 && // '0'-'9'
      byte3 === 124 && // '|'
      byte4 === 45; // '-'

    if (isProgressMessage) {
      // 保存进度消息之前的数据
      if (searchPos > writeStart) {
        segments.push(chunk.slice(writeStart, searchPos));
      }

      // 解析进度值：00 代表 100%，01-99 是实际进度
      const rawProgress = (byte1 - 48) * 10 + (byte2 - 48);
      lastProgressValue = rawProgress === 0 ? 100 : rawProgress;

      // 跳过进度消息（4字节）
      writeStart = searchPos + 4;
      searchPos = searchPos + 4;
    } else {
      searchPos++;
    }
  }

  // 添加剩余的数据
  if (writeStart < chunk.length) {
    segments.push(chunk.slice(writeStart));
  }

  // 合并所有数据段
  let cleanData: Uint8Array;
  if (segments.length === 0) {
    cleanData = new Uint8Array(0);
  } else if (segments.length === 1) {
    cleanData = segments[0];
  } else {
    const totalLength = segments.reduce((sum, seg) => sum + seg.length, 0);
    cleanData = new Uint8Array(totalLength);
    let offset = 0;
    for (const segment of segments) {
      cleanData.set(segment, offset);
      offset += segment.length;
    }
  }

  return {
    cleanData,
    progress: lastProgressValue,
  };
}

export function processSchema<E extends ExportSchemaItem = ExportSchemaItem>(
  data: Record<string, unknown>,
  items: E[],
  emptyStr: string = '--',
  withUnit?: boolean
): Record<string, string> {
  const result: Record<string, string> = {};

  // 递归处理 schema 中定义的所有字段
  function processSchemaItems(items: ExportSchemaItem[]) {
    items.forEach((item) => {
      if (item.children && item.children.length > 0) {
        // 递归处理子项
        processSchemaItems(item.children);
      } else {
        // 处理叶子节点
        let value = get(data, item.key);

        // 处理空值
        if (value === undefined || value === null || (typeof value === 'number' && isNaN(value))) {
          result[item.key] = emptyStr;
          return;
        }

        // 应用格式化函数
        if (typeof item.format === 'function') {
          try {
            value = item.format(value);
          } catch {
            value = emptyStr;
          }
        }

        // 添加单位
        if (withUnit && item.unit && value !== emptyStr) {
          value = `${value}${item.unit}`;
        }

        result[item.key] = String(value);
      }
    });
  }

  processSchemaItems(items);
  return result;
}

type XlsxColumnWithKey = XlsxColumn & { key: string };

/**
 * 生成 Excel 模板文件（支持多层表头和列合并）
 * @param header 表头配置（支持多层）
 * @param sheetName 工作表名称
 * @returns Excel 文件的 ArrayBuffer
 *
 * @example
 * ```typescript
 * const buffer = await writeExcel(header, '车辆列表');
 * return new Response(buffer, {
 *   headers: {
 *     'Content-Type': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
 *     'Content-Disposition': 'attachment; filename="template.xlsx"',
 *   },
 * });
 * ```
 */
export async function writeExcel(
  header: XlsxColumn[][],
  sheetName: string = 'Sheet1'
): Promise<ArrayBuffer> {
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(sheetName);

  // 提取数据列配置（只取最后一层的非占位符列）
  const lastLayer = header[header.length - 1];
  const dataColumns = lastLayer.filter((col) => !col.isPlaceholder && col.key);

  // 设置列宽
  worksheet.columns = dataColumns.map((col) => ({
    key: col.key!,
    width: col.width || 30,
  }));

  // 添加表头行（支持多层表头和列合并）
  header.forEach((headerRow: XlsxColumn[], rowIndex: number) => {
    const row = worksheet.getRow(rowIndex + 1);
    let colIndex = 1;

    headerRow.forEach((col: XlsxColumn) => {
      // 跳过占位符
      if (col.isPlaceholder) {
        colIndex++;
        return;
      }

      const cell = row.getCell(colIndex);
      cell.value = col.title || '';

      // 处理列合并（colspan）
      const colSpan = col.colSpan || 1;
      if (colSpan > 1) {
        // 合并单元格：从当前列到 (当前列 + colSpan - 1)
        worksheet.mergeCells(rowIndex + 1, colIndex, rowIndex + 1, colIndex + colSpan - 1);
      }

      colIndex += colSpan;
    });

    row.commit();
  });

  // 生成 Excel 文件并返回 ArrayBuffer
  return await workbook.xlsx.writeBuffer();
}

/**
 * 读取 Excel 文件并返回数据数组
 * @param fileData 文件数据（ArrayBuffer）
 * @param columns 列定义
 * @returns 数据对象数组
 */
export async function readExcel(
  fileData: ArrayBuffer,
  header: XlsxColumn[][],
  sheetName?: string
): Promise<any[]> {
  const workbook = new ExcelJS.Workbook();
  await workbook.xlsx.load(fileData);

  // 根据 sheetName 选择工作表
  let worksheet: ExcelJS.Worksheet;
  if (sheetName) {
    const foundWorksheet = workbook.getWorksheet(sheetName);
    if (!foundWorksheet) {
      throw new Error(`Excel 文件中没有找到名为 "${sheetName}" 的工作表`);
    }
    worksheet = foundWorksheet;
  } else {
    // 默认使用第一个工作表
    worksheet = workbook.worksheets[0];
  }

  const rows: any[] = [];

  // 计算表头行数（与配置的 header 层数一致）
  const headerRowCount = header.length;

  // 提取数据列配置（包含 key 和 header 的映射）
  const dataColumns = header[headerRowCount - 1].filter(
    (col) => col.key && !col.isPlaceholder
  ) as XlsxColumnWithKey[];

  // 读取 excel 数据表头
  const headerRow = worksheet.getRow(headerRowCount);
  const columnMapping: { [colIndex: number]: XlsxColumnWithKey } = {};

  const expectedTitles = dataColumns.map((col) => col.title);
  const actualTitles: string[] = [];

  // 建立列索引映射（列号 -> 列定义）
  headerRow.eachCell({ includeEmpty: false }, (cell, colIndex) => {
    const headerText = String(cell.value || '').trim();
    actualTitles.push(headerText);
    const column = dataColumns.find((c) => c.title === headerText);
    if (column) {
      columnMapping[colIndex] = column;
    }
  });

  // 检查上传文件是否包含所有必需的列
  const missingTitles = difference(expectedTitles, actualTitles);
  if (missingTitles.length > 0) {
    throw new Error(`Excel 文件缺少以下必需的列：${missingTitles.join(', ')}`);
  }

  // 读取数据行
  worksheet.eachRow({ includeEmpty: false }, (row, rowIndex) => {
    // 跳过所有表头
    if (rowIndex <= headerRowCount) return;

    const rowData: any = {};

    row.eachCell({ includeEmpty: false }, (cell, colIndex) => {
      const column = columnMapping[colIndex];
      if (column) {
        let value = cell.value;

        // 处理日期类型
        if (value instanceof Date) {
          rowData[column.key] = value;
        } else if (value && typeof value === 'object' && 'richText' in value) {
          // 处理富文本对象（ExcelJS 特殊格式）
          rowData[column.key] = (value as any).richText.map((rt: any) => rt.text).join('');
        } else if (value !== null && value !== undefined) {
          // 处理普通值
          if (typeof value === 'string') {
            value = value.trim();
          }
          rowData[column.key] = value;
        }
      }
    });

    // 将读取的行数据添加到结果数组中
    rows.push(rowData);
  });

  return rows;
}
